import { useState, useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Chip, Button,
  TextField, Grid, FormControlLabel, Switch, MenuItem, Avatar,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import api from '../../services/api';
import {
  getGroceryStores, getGroceryProducts, placeGroceryOrder,
  updateGroceryOrderStatus, rateGroceryOrder, createGroceryStore, updateGroceryStore, deleteGroceryStore,
  deleteGroceryProduct,
} from '../../services/businessService';
import FormFileUpload from '../../components/ui/forms/FormFileUpload';
import { resolveAssetUrl } from '../../utils/assetUrl';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import DataTableCard from '../../components/ui/DataTableCard';
import EnhancedDataGrid from '../../components/ui/EnhancedDataGrid';
import RowActions from '../../components/ui/RowActions';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import EntityOperationsPanel, { QuickBookDialog } from '../../components/operations/EntityOperationsPanel';
import { formatMoney } from '../../utils/currency';
import GroceryStoreAddDrawer, {
  emptyGroceryStoreForm, storeFormToPayload, storeRowToForm, type GroceryStoreFormState,
} from '../../components/grocery/GroceryStoreAddDrawer';

export default function GroceryOrdersPage() {
  const queryClient = useQueryClient();
  const [bookOpen, setBookOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['grocery-orders'],
    queryFn: async () => {
      const res = await api.get('/grocery/orders');
      return res.data;
    },
  });

  const { data: stores = [] } = useQuery({
    queryKey: ['grocery-stores-list'],
    queryFn: () => getGroceryStores(true),
  });

  const storeId = stores[0]?._id;
  const { data: products = [] } = useQuery({
    queryKey: ['grocery-products-book', storeId],
    queryFn: () => getGroceryProducts(storeId),
    enabled: !!storeId,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['grocery-orders'] });

  const columns: GridColDef[] = [
    { field: 'orderNumber', headerName: 'Order #', width: 130 },
    { field: 'storeId', headerName: 'Store', flex: 1, valueGetter: (_v, row) => row.storeId?.name || '-' },
    { field: 'customerId', headerName: 'Customer', flex: 1, valueGetter: (_v, row) => row.customerId?.firstName || '-' },
    {
      field: 'status', headerName: 'Status', width: 110,
      renderCell: (params) => <Chip label={(params.value as string).replace(/_/g, ' ')} size="small" />,
    },
    { field: 'total', headerName: 'Total', width: 90, valueFormatter: (v) => `${formatMoney(v)}` },
    { field: 'paymentMethod', headerName: 'Pay', width: 80 },
    {
      field: 'actions', headerName: 'Actions', width: 280, sortable: false,
      renderCell: (params) => (
        <EntityOperationsPanel
          entityType="grocery_order"
          entityId={params.row._id}
          currentStatus={params.row.status}
          terminalStatuses={['delivered', 'cancelled', 'refunded']}
          onStatusChange={async (status) => {
            await updateGroceryOrderStatus(params.row._id, status);
            invalidate();
          }}
          onRate={params.row.status === 'delivered' && !params.row.metadata?.rating
            ? async (rating, review) => {
              await rateGroceryOrder(params.row._id, rating, review);
              invalidate();
            }
            : undefined}
        />
      ),
    },
    { field: 'createdAt', headerName: 'Date', width: 160, valueFormatter: (v) => new Date(v as string).toLocaleString() },
  ];

  return (
    <PageShell>
      <PageHeader
        eyebrow="Grocery"
        title="Grocery Orders"
        subtitle="Picking, packing, delivery workflow with stock-backed ordering."
        actions={<Button variant="contained" startIcon={<Add />} onClick={() => setBookOpen(true)}>Place Order</Button>}
      />
      <DataTableCard title="Order Queue">
        <EnhancedDataGrid
          rows={data?.data || []}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row._id as string}
          autoHeight
          exportFileName="grocery-orders"
          statusField="status"
          statusOptions={[
            { value: 'confirmed', label: 'Confirmed' },
            { value: 'delivering', label: 'Delivering' },
            { value: 'delivered', label: 'Delivered' },
            { value: 'cancelled', label: 'Cancelled' },
          ]}
        />
      </DataTableCard>

      <QuickBookDialog
        open={bookOpen}
        title="Place Grocery Order"
        onClose={() => setBookOpen(false)}
        fields={[
          { key: 'storeId', label: 'Store', type: 'select', options: stores.map((s: { _id: string; name: string }) => ({ value: s._id, label: s.name })) },
          { key: 'productId', label: 'Product', type: 'select', options: products.map((p: { _id: string; name: string }) => ({ value: p._id, label: p.name })) },
          { key: 'quantity', label: 'Quantity', type: 'number' },
          { key: 'address', label: 'Delivery Address' },
          { key: 'paymentMethod', label: 'Payment', type: 'select', options: [{ value: 'cash', label: 'Cash' }, { value: 'wallet', label: 'Wallet' }] },
        ]}
        defaults={{ storeId, quantity: '1', paymentMethod: 'cash' }}
        onSubmit={async (form) => {
          const product = products.find((p: { _id: string }) => p._id === form.productId) || products[0];
          if (!product) throw new Error('No product');
          await placeGroceryOrder({
            storeId: form.storeId || storeId,
            items: [{ productId: product._id, quantity: Number(form.quantity) || 1 }],
            deliveryAddress: { street: form.address, city: 'Bangalore', country: 'IN' },
            paymentMethod: form.paymentMethod || 'cash',
          });
          invalidate();
        }}
      />
    </PageShell>
  );
}

export function GroceryStoresPage({ showHeader = true }: { showHeader?: boolean } = {}) {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ _id: string; name: string } | null>(null);
  const [editing, setEditing] = useState<Record<string, unknown> | null>(null);
  const [form, setForm] = useState<GroceryStoreFormState>(emptyGroceryStoreForm());

  const { data, isLoading } = useQuery({
    queryKey: ['grocery-stores-admin'],
    queryFn: () => getGroceryStores(true),
    refetchInterval: 15_000,
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = storeFormToPayload(form);
      if (editing?._id) return updateGroceryStore(editing._id as string, payload);
      return createGroceryStore(payload);
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['grocery-stores-admin'] }); setDialogOpen(false); setEditing(null); },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteGroceryStore,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['grocery-stores-admin'] }); setDeleteTarget(null); },
  });

  const openAdd = () => {
    setEditing(null);
    setForm(emptyGroceryStoreForm());
    setDialogOpen(true);
  };

  const columns: GridColDef[] = [
    {
      field: 'logo', headerName: '', width: 56, sortable: false,
      renderCell: (p) => <Avatar src={resolveAssetUrl(p.row.logo)} variant="rounded" sx={{ width: 36, height: 36 }} />,
    },
    { field: 'name', headerName: 'Store', flex: 1.2, minWidth: 140 },
    { field: 'storeType', headerName: 'Type', width: 110 },
    {
      field: 'city', headerName: 'City', width: 110,
      valueGetter: (_v, row) => (row.address as { city?: string })?.city || '—',
    },
    { field: 'phone', headerName: 'Phone', width: 120 },
    { field: 'deliveryRadius', headerName: 'Radius', width: 80, valueFormatter: (v) => v != null ? `${v} km` : '—' },
    { field: 'minOrderAmount', headerName: 'Min Order', width: 100, valueFormatter: (v) => `${formatMoney(v)}` },
    {
      field: 'verificationStatus', headerName: 'Status', width: 110,
      renderCell: (p) => {
        const v = String(p.value || 'pending');
        const color = v === 'approved' || v === 'verified' ? 'success' : v === 'rejected' || v === 'blocked' ? 'error' : 'warning';
        return <Chip label={v} size="small" color={color as 'success' | 'error' | 'warning'} />;
      },
    },
    { field: 'rating', headerName: 'Rating', width: 80 },
    { field: 'isOpen', headerName: 'Open', width: 80, renderCell: (p) => <Chip label={p.row.isOpen === true ? 'Open' : 'Closed'} size="small" color={p.row.isOpen === true ? 'success' : 'default'} /> },
    { field: 'isActive', headerName: 'Active', width: 80, renderCell: (p) => <Chip label={p.value !== false ? 'Yes' : 'No'} size="small" variant="outlined" /> },
    {
      field: 'actions', headerName: 'Actions', width: 100, sortable: false,
      renderCell: (p) => <RowActions onEdit={() => { setEditing(p.row); setForm(storeRowToForm(p.row)); setDialogOpen(true); }} onDelete={() => setDeleteTarget({ _id: p.row._id, name: p.row.name })} />,
    },
  ];

  const body = (
    <>
      {showHeader ? (
        <PageHeader
          eyebrow="Grocery"
          title="Stores"
          subtitle="Full store master — identity, location, compliance, delivery settings, timing, and revenue."
          actions={<Button variant="contained" startIcon={<Add />} onClick={openAdd}>Add Store</Button>}
        />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" fontWeight={800}>Stores</Typography>
            <Typography variant="body2" color="text.secondary">
              Add grocery stores and manage previously added locations.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Add Store
          </Button>
        </Box>
      )}
      <DataTableCard title="Store Catalog">
        <EnhancedDataGrid rows={data || []} columns={columns} loading={isLoading} getRowId={(r) => r._id as string} autoHeight exportFileName="grocery-stores" />
      </DataTableCard>
      <GroceryStoreAddDrawer
        open={dialogOpen}
        editing={editing}
        form={form}
        onFormChange={setForm}
        onClose={() => setDialogOpen(false)}
        onSubmit={() => saveMutation.mutate()}
        loading={saveMutation.isPending}
        errorMessage={saveMutation.error ? (saveMutation.error as Error).message : null}
      />
      <ConfirmDialog open={!!deleteTarget} title="Remove store?" message={`Deactivate "${deleteTarget?.name}"?`} onCancel={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)} loading={deleteMutation.isPending} />
    </>
  );

  return showHeader ? <PageShell>{body}</PageShell> : body;
}

export function GroceryProductsPage({ showHeader = true }: { showHeader?: boolean } = {}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [storeId, setStoreId] = useState(searchParams.get('storeId') || '');
  const [deleteTarget, setDeleteTarget] = useState<{ _id: string; name: string } | null>(null);

  const { data: stores = [] } = useQuery<Array<{ _id: string; name: string }>>({ queryKey: ['grocery-stores-admin'], queryFn: () => getGroceryStores(true) });
  const activeStoreId = storeId || String(stores[0]?._id || '');

  useEffect(() => {
    const sid = searchParams.get('storeId');
    if (sid) setStoreId(sid);
  }, [searchParams]);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['grocery-products-admin', activeStoreId],
    queryFn: () => getGroceryProducts(activeStoreId),
    enabled: !!activeStoreId,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteGroceryProduct,
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['grocery-products-admin'] }); setDeleteTarget(null); },
  });

  const columns: GridColDef[] = [
    {
      field: 'image', headerName: '', width: 56, sortable: false,
      renderCell: (p) => <Avatar src={resolveAssetUrl(p.row.image)} variant="rounded" sx={{ width: 36, height: 36 }} />,
    },
    { field: 'name', headerName: 'Product', flex: 1 },
    { field: 'category', headerName: 'Category', width: 120 },
    { field: 'sku', headerName: 'SKU', width: 110 },
    { field: 'brand', headerName: 'Brand', width: 100 },
    { field: 'price', headerName: 'Price', width: 80, valueFormatter: (v) => `${formatMoney(v)}` },
    { field: 'stock', headerName: 'Stock', width: 80 },
    { field: 'isActive', headerName: 'Active', width: 80, renderCell: (p) => <Chip label={p.value !== false ? 'Yes' : 'No'} size="small" variant="outlined" /> },
    {
      field: 'actions', headerName: 'Actions', width: 100, sortable: false,
      renderCell: (p) => (
        <RowActions
          onEdit={() => navigate(`/grocery/products/${p.row._id}/edit?storeId=${activeStoreId}`)}
          onDelete={() => setDeleteTarget({ _id: p.row._id, name: p.row.name })}
        />
      ),
    },
  ];

  const addProductBtn = (
    <Button
      variant="contained"
      startIcon={<Add />}
      disabled={!activeStoreId}
      onClick={() => navigate(`/grocery/products/new?storeId=${activeStoreId}`)}
      sx={{ textTransform: 'none', fontWeight: 700 }}
    >
      Add Product
    </Button>
  );

  const body = (
    <>
      {showHeader ? (
        <PageHeader
          eyebrow="Grocery"
          title="Products & Inventory"
          subtitle="4-tab Grocery Add wizard — masters drive category, brand, variant, tax, and images."
          actions={addProductBtn}
        />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" fontWeight={800}>Products & Inventory</Typography>
            <Typography variant="body2" color="text.secondary">
              Add products for a store and manage previously added items.
            </Typography>
          </Box>
          {addProductBtn}
        </Box>
      )}
      <Box mb={2}>
        <TextField select size="small" label="Store" value={activeStoreId} onChange={(e) => setStoreId(e.target.value)} sx={{ minWidth: 220 }}>
          {stores.length === 0 && <MenuItem value="">No stores yet — add a store first</MenuItem>}
          {stores.map((s) => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
        </TextField>
      </Box>
      <DataTableCard title="Product Catalog">
        <EnhancedDataGrid rows={products} columns={columns} loading={isLoading} getRowId={(r) => r._id as string} autoHeight exportFileName="grocery-products" />
      </DataTableCard>
      <ConfirmDialog open={!!deleteTarget} title="Remove product?" message={`Deactivate "${deleteTarget?.name}"?`} onCancel={() => setDeleteTarget(null)} onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget._id)} loading={deleteMutation.isPending} />
    </>
  );

  return showHeader ? <PageShell>{body}</PageShell> : body;
}
